import * as Icons from '@ant-design/icons';
import { Button, Input, Modal, Space, Tabs, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';

type IconKey = keyof typeof Icons;

export type IconModalProps = {
  value?: string; // 例如：'UserOutlined'
  onChange?: (iconKey: string) => void;

  title?: string;
  placeholder?: string;
  disabled?: boolean;

  /** 触发按钮文本 */
  triggerText?: string;
};

/** 过滤掉不是图标组件的导出（IconProvider 等） */
function isIconComponentExport(name: string) {
  if (!name) return false;
  // 这些不是图标
  const blacklist = new Set([
    'default',
    'createFromIconfontCN',
    'getTwoToneColor',
    'setTwoToneColor',
    'IconProvider',
  ]);
  if (blacklist.has(name)) return false;

  // 图标命名约定
  return /^(.*)(Outlined|Filled|TwoTone)$/.test(name);
}

function pickIconsByType(type: 'Outlined' | 'Filled' | 'TwoTone') {
  return Object.keys(Icons)
    .filter((k) => isIconComponentExport(k) && k.endsWith(type))
    .sort();
}

const OUTLINED = pickIconsByType('Outlined');
const FILLED = pickIconsByType('Filled');
const TWOTONE = pickIconsByType('TwoTone');

export function IconModal(props: IconModalProps) {
  const {
    value,
    onChange,
    title = '选择图标',
    placeholder = '搜索图标名，比如 User / Setting / Table…',
    disabled,
    triggerText = '选择图标',
  } = props;

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [tab, setTab] = useState<'Outlined' | 'Filled' | 'TwoTone'>('Outlined');

  const SelectedIcon = value ? (Icons[value as IconKey] as any) : null;

  const currentList = useMemo(() => {
    const list =
      tab === 'Outlined' ? OUTLINED : tab === 'Filled' ? FILLED : TWOTONE;
    if (!keyword.trim()) return list;

    const kw = keyword.trim().toLowerCase();
    return list.filter((name) => name.toLowerCase().includes(kw));
  }, [keyword, tab]);

  const gridCols = 10;
  const cellSize = 44;

  return (
    <>
      {/* <Button
                disabled={disabled}
                onClick={() => setOpen(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
                {SelectedIcon ? <SelectedIcon /> : null}
                {value ? value : triggerText}
            </Button> */}

      <Button
        disabled={disabled}
        onClick={() => setOpen(true)}
        // style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <Space size={6}>
          {SelectedIcon ? <SelectedIcon /> : null}
          {value ? value : triggerText}

          {/* 有值才显示清除 */}
          {!!value && !disabled && (
            <Tooltip title="清除图标">
              <Icons.CloseCircleFilled
                style={{ cursor: 'pointer', color: 'rgba(0,0,0,.45)' }}
                onClick={(e) => {
                  e.stopPropagation(); // 不触发 Button 打开弹窗
                  onChange?.(''); // 清空（ProForm 会同步清空字段）
                }}
                onMouseDown={(e) => e.preventDefault()} // 防止抢焦点导致奇怪行为
              />
            </Tooltip>
          )}
        </Space>
      </Button>

      <Modal
        title={title}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={610}
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <Input
            allowClear
            value={keyword}
            placeholder={placeholder}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>

        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as any)}
          items={[
            { key: 'Outlined', label: '线框风格' },
            { key: 'Filled', label: '实底风格' },
            { key: 'TwoTone', label: '双色风格' },
          ]}
        />

        <div
          style={{
            maxHeight: 520,
            overflow: 'auto',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: 12,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridCols}, ${cellSize}px)`,
              gap: 10,
              justifyContent: 'flex-start',
            }}
          >
            {currentList.map((name) => {
              const IconComp = Icons[name as IconKey] as any;
              const active = value === name;

              return (
                <Tooltip key={name} title={name} placement="bottom">
                  <button
                    type="button"
                    onClick={() => {
                      onChange?.(name);
                      setOpen(false);
                    }}
                    style={{
                      width: cellSize,
                      height: cellSize,
                      borderRadius: 8,
                      border: active
                        ? '1px solid #1677ff'
                        : '1px solid #f0f0f0',
                      background: active ? 'rgba(22,119,255,0.08)' : '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                    }}
                  >
                    <IconComp />
                  </button>
                </Tooltip>
              );
            })}
          </div>

          {currentList.length === 0 ? (
            <div style={{ padding: 24, color: '#999' }}>没有匹配到图标</div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
